import {
  useWeb3React,
  Web3ReactHooks,
  Web3ReactProvider,
} from "@web3-react/core";
import { createMachine } from "xstate";
import useOrderedConnections from "./useOrderedConnections";
import { Connector } from "@web3-react/types";
import { useEagerlyConnect } from "./useEagerlyConnect";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useMachine } from "@xstate/react";
import {
  disconnect as _disconnect,
  metaMaskWallet,
  SupportedWallet,
  supportedWallets,
} from "./wallets";
import { usePersistedSelectedWallet } from "./usePersistedSelectedWallet";
import { useMaybeBlockUser } from "./blocked/use-maybe-block-user";
import { DEFAULT_BLOCKED_PATH } from "./constants";
import { getConnectionName } from "./utils";
import {
  createClientProvider,
  durationToMilliseconds,
  fetchProviderStatus,
  isNotNullNorUndefined,
  mapNullable,
} from "@silo/utils";
import { getNetworkConnection } from "./connectors";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useImpersonatedAccount } from "./useImpersonatedAccount";
import {
  Address,
  ChainIdLegacy,
  ChecksumAddress,
  ClientProvider,
  DEFAULT_PROTOCOL_KEY,
  ProtocolInstance,
  ProtocolKey,
  ProviderPriority,
  SigningProvider,
  SupportedChainId,
  getProtocolInstanceChainId,
  isAddress,
  isChecksumAddress,
  isSupportedChainId,
  toSigningProvider,
} from "@silo/types";
import {
  SetProtocolInstance,
  useProtocolInstance,
} from "./protocol-instance/useProtocolInstance";
import { switchChain } from "./chain/switchChain";
import { usePrevious } from "@silo/ui";

const userMachine = createMachine({
  id: "user",
  initial: "unknown",
  states: {
    unknown: {
      on: {
        CONNECT: { target: "connected" },
        DISCONNECT: { target: "disconnected" },
      },
    },
    disconnected: {
      on: {
        CONNECT: { target: "connected" },
      },
    },
    restricted: {
      entry: ["disconnect"],
    },
    connected: {
      on: {
        DISCONNECT: { target: "disconnected" },
      },
    },
  },
  on: {
    RESTRICT: { target: "restricted" },
  },
});
interface SiloWeb3ProviderProps {
  blockedPath?: string;
  children?: ReactNode;
}

export function SiloWeb3Provider({
  blockedPath = DEFAULT_BLOCKED_PATH,
  children,
}: SiloWeb3ProviderProps): JSX.Element {
  const [selectedWallet] = usePersistedSelectedWallet();
  const connections = useOrderedConnections(selectedWallet?.type);
  const connectors: [Connector, Web3ReactHooks][] = connections.map(
    ({ hooks, connector }) => [connector, hooks]
  );

  const key = useMemo(
    () => connections.map(({ type }) => getConnectionName(type)).join("-"),
    [connections]
  );

  return (
    <Web3ReactProvider connectors={connectors} key={key}>
      <InnerProvider blockedPath={blockedPath}>{children}</InnerProvider>
    </Web3ReactProvider>
  );
}

export default SiloWeb3Provider;

export interface SiloWeb3 {
  clientProvider: ClientProvider | undefined;
  signingProvider: SigningProvider | undefined;
  chainId: ChainIdLegacy | undefined;
  supportedChainId: SupportedChainId | undefined;
  protocolInstance: ProtocolInstance | undefined;
  setProtocolInstance: SetProtocolInstance;
  isAttemptingChainUpdate: boolean;
  isInvalidChain: boolean;
  /**
   * @deprecated use `accountV2`
   */
  account: ChecksumAddress | undefined;
  accountV2: Address | undefined;
  isActive: boolean;
  disconnect: () => void;
  status: UserStatus;
  activeWallet: SupportedWallet | undefined;
  isConnected: boolean;
  isNotConnected: boolean;
  connector: Connector;
  isImpersonating: boolean;
  ensName: UseQueryResult<string | null>;
  switchChain: (chainId: SupportedChainId) => Promise<void>;
  providerPriority?: ProviderPriority;
  protocolKey: ProtocolKey;
}

export type UserStatus =
  | "unknown"
  | "disconnected"
  | "restricted"
  | "connected";

const SiloWeb3Context = createContext<SiloWeb3>(null!);
interface InnerProviderProps {
  blockedPath: string;
  children?: ReactNode;
}

export function InnerProvider({
  blockedPath,
  children,
}: InnerProviderProps): JSX.Element {
  useEagerlyConnect(getNetworkConnection);
  const {
    provider,
    chainId,
    account: rawAccount,
    isActive,
    connector,
  } = useWeb3React();

  const account =
    rawAccount && isChecksumAddress(rawAccount) ? rawAccount : undefined;

  const accountV2 = isAddress(rawAccount) ? rawAccount : undefined;

  const [, setPersistedSelectedWallet, clearPersistedSelectedWallet] =
    usePersistedSelectedWallet();

  const disconnect = () => {
    if (!account) return;
    return _disconnect(connector);
  };

  const { impersonatedAccount, disconnectImpersonatedAccount } =
    useImpersonatedAccount({
      disconnectAccount: disconnect,
      isActive,
      account,
    });

  const impersonatedAccountV2 = isAddress(impersonatedAccount)
    ? impersonatedAccount
    : undefined;

  const { data } = useQuery({
    queryKey: ["providerPriority"],
    queryFn: fetchProviderStatus,
    refetchInterval: durationToMilliseconds({ minutes: 10 }),
  });

  const providerPriority = data?.priority;

  useEffect(() => {
    console.log("Provider priority: ", providerPriority);
  }, [providerPriority]);

  const clientProvider = useMemo(() => {
    if (
      isSupportedChainId(chainId) &&
      isNotNullNorUndefined(providerPriority)
    ) {
      return createClientProvider(chainId, providerPriority);
    }

    return undefined;
  }, [chainId, providerPriority]);

  const signingProvider = mapNullable(provider, toSigningProvider);

  const effectiveAccount = impersonatedAccount ?? account;
  const effectiveAccountV2 = impersonatedAccountV2 ?? accountV2;

  const ensResult = useENSAddress(effectiveAccount, clientProvider);

  const [state, send] = useMachine(userMachine, {
    actions: {
      disconnect,
    },
  });

  useMaybeBlockUser(effectiveAccountV2, blockedPath);

  useEffect(() => {
    if (!isActive) return;

    if (account) {
      send("CONNECT");
    } else {
      send("DISCONNECT");
    }
  }, [isActive, account, send]);

  const activeWallet = supportedWallets.find(
    (wallet) => wallet.connector === connector
  );
  const previousWallet = usePrevious(activeWallet);

  useEffect(() => {
    if (activeWallet) {
      setPersistedSelectedWallet(activeWallet.type);
    }

    if (!activeWallet && previousWallet) {
      clearPersistedSelectedWallet();
    }
  }, [
    activeWallet,
    previousWallet,
    setPersistedSelectedWallet,
    clearPersistedSelectedWallet,
  ]);

  const _switchChain = useCallback(
    (_chainId: SupportedChainId) => switchChain(connector, _chainId),
    [connector]
  );

  const supportedChainId = isSupportedChainId(chainId) ? chainId : undefined;
  const {
    protocolInstance,
    setProtocolInstance,
    isAttemptingChainUpdate,
    isInvalidChain,
  } = useProtocolInstance({
    chainId,
    switchChain: _switchChain,
  });

  const status = state.toStrings().shift() as UserStatus;
  const isConnected = status === "connected";
  const isNotConnected = !isConnected;

  const impersonationOverrides: Partial<SiloWeb3> = impersonatedAccount
    ? {
        account: impersonatedAccount,
        isActive: true,
        disconnect: disconnectImpersonatedAccount,
        status: "connected",
        activeWallet: metaMaskWallet,
        isConnected: true,
        isNotConnected: false,
        isImpersonating: true,
      }
    : {};

  const username = ensResult.data ?? undefined;
  const wallet = activeWallet?.name;

  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;

  return (
    <SiloWeb3Context.Provider
      value={{
        clientProvider,
        signingProvider,
        account,
        accountV2,
        isActive,
        disconnect,
        status,
        activeWallet,
        isConnected,
        isNotConnected,
        chainId: mapNullable(protocolInstance, getProtocolInstanceChainId),
        supportedChainId,
        protocolInstance,
        setProtocolInstance,
        isAttemptingChainUpdate,
        isInvalidChain,
        connector,
        isImpersonating: false,
        ensName: ensResult,
        switchChain: _switchChain,
        providerPriority: providerPriority,
        protocolKey,
        ...impersonationOverrides,
      }}
    >
      {children}
    </SiloWeb3Context.Provider>
  );
}

const useENSAddress = (
  account: ChecksumAddress | undefined,
  clientProvider: ClientProvider | undefined
) => {
  return useQuery({
    queryKey: ["ENS", account!, clientProvider!] as const,
    queryFn: ({ queryKey: [, _account, _clientProvider] }) => {
      return _clientProvider.lookupAddress(_account);
    },
    enabled: !!account && !!clientProvider,
  });
};

export const useSiloWeb3 = () => useContext(SiloWeb3Context);
